import Icon from '@components/icon/Icon';
import searchMarker from '@components/map/assets/search-marker-icon.png';
import {
  mdiClose,
  mdiFolderUploadOutline,
  mdiLayersTriple,
  mdiMagnify,
  mdiMapMarker,
  mdiMinus,
  mdiPlus,
  mdiTrashCanOutline,
} from '@mdi/js';

import _startCase from 'lodash/startCase';

import { useApp, useJpa, useModal, useRequest, useTableInput } from '@stores';
import { getAppStore } from '@utils/store-reference';

import { queryClient } from '@api/queryClient';
import { AorQueries } from '@api/services/aor';
import { formatJpaRequestAnswerSet } from '@api/services/jpa/helpers';
import { LOOKUP_QUERY_KEYS } from '@api/services/lookup/query';
import { formatNonPcnAnswerSet } from '@api/services/non-pcn/helpers';
import { formatNprAnswerSet } from '@api/services/npr/helpers';
import { saveArChanges } from '@api/services/aquatic-resources/helpers';

import { PNmapUseTipsList } from '@components/map/helper';
import { convertArTableInputDataToApiData } from '@components/table-input/tableInput.helpers.ars';
import { filterNullEmptyObjects, formatCoordFlt } from '@utils/helpers';
import { formatJdPayload } from './JDForm/JDForm.helpers';
import { formatPermitApplicationAnswers } from './PermitsForm/generalProjectInformation/PermitsForm.helper';
import { formatVcPayload } from './ViolationsForms/ViolationsForm.helpers';

import {
  ContactTypes,
  ErrorMessages,
  GeometryTypes,
  LoadingMessages,
  RequestMap,
  RequestStatus,
  RequestTypeIds,
  stateNamesByCode,
} from '@utils/enums';
import {
  addressRegex,
  addressTwoRegex,
  cityRegex,
  countryCodeRegex,
  emailRegex,
  parcelsRegex,
  phoneExtRegex,
  phoneRegex,
  usPhoneRegex,
  zipCodeRegex,
} from '@utils/regex';
import { fullscreenSVG, polyLineSVG, polygonSVG } from '@utils/svg';

import statusModal from '@pages/Forms/components/modals/statusModal';
import { resolveNprConfigForRequest } from '@pages/Forms/registry/configs/npr/nprConfig';

const getCachedStateTableData = () =>
  queryClient.getQueryData([LOOKUP_QUERY_KEYS.GET_ALL_TABLES])?.StateRegulations ?? [];

export const getStateAbbreviations = (stateTableData = getCachedStateTableData()) => {
  return stateTableData.reduce((acc, { stateABBR, state } = {}) => {
    if (stateABBR && state) {
      acc[stateABBR] = state.toUpperCase();
    }
    return acc;
  }, {});
};

export const getStateAbbrProperCase = (stateTableData = getCachedStateTableData()) => {
  const stateAbbreviations = getStateAbbreviations(stateTableData);

  return Object.keys(stateAbbreviations).reduce((acc, key) => {
    acc[key] = _startCase(stateAbbreviations[key].toLowerCase()).replace(' The ', ' the ').replace(' Of ', ' of ');
    return acc;
  }, {});
};

export const salutations = [
  { value: 'Dr.', text: 'Dr.' },
  { value: 'Miss.', text: 'Miss.' },
  { value: 'Mr.', text: 'Mr.' },
  { value: 'Mrs.', text: 'Mrs.' },
  { value: 'Ms.', text: 'Ms.' },
  { value: 'Mx.', text: 'Mx.' },
];

export const phonetypes = [
  { value: 'Residence', text: 'Residence' },
  { value: 'Business', text: 'Business' },
  { value: 'Primary', text: 'Primary' },
  { value: 'Secondary', text: 'Secondary' },
  { value: 'Mobile', text: 'Mobile' },
];

// NOTE: make sure any special characters are removed from the values
export const jdDocTypes = [
  { value: 'Aerial Photographs', text: 'Aerial Photograph(s)' },
  { value: 'Applicable Supporting Case Law', text: 'Applicable Supporting Case Law' },
  { value: 'Applicable Supporting Scientific Literature', text: 'Applicable Supporting Scientific Literature' },
  { value: 'Automated Wetland Datasheet', text: 'Automated Wetland Datasheet (excel file)' },
  { value: 'FEMA FIRM Maps', text: 'FEMA/FIRM Maps' },
  { value: 'Maps Plans Plots or Plats', text: 'Maps, Plans, Plots or Plats' },
  { value: 'National Wetland Inventory Map', text: 'National Wetland Inventory Map' },
  { value: 'OHWM Datasheets', text: 'OHWM Datasheets' },
  { value: 'Other Information', text: 'Other Information' },
  { value: 'Site Photographs', text: 'Site Photograph(s)' },
  { value: 'State Local Aquatic Resource Form', text: 'State/Local Aquatic Resource Form' },
  { value: 'State Local Wetland Inventory Map', text: 'State/Local Wetland Inventory Map' },
  { value: 'US Geological Survey Map', text: 'U.S. Geological Survey Map' },
  { value: 'USACE Navigable Waters Study', text: 'USACE Navigable Waters Study' },
  { value: 'USDS NRCS Soil Survey Map', text: 'USDS NRCS Soil Survey Map' },
  { value: 'USGS Hydrologic Unit Code Map', text: 'USGS Hydrologic Unit Code Map' },
  { value: 'USGS National Hydrography Dataset Map', text: 'USGS National Hydrography Dataset Map' },
  { value: 'Wetland Datasheets', text: 'Wetland Datasheets' },
].sort((a, b) => a.text.localeCompare(b.text));

export const standardDocTypes = [
  { value: 'Aerial Photographs', text: 'Aerial Photograph(s)' },
  { value: 'Applicable Supporting Case Law', text: 'Applicable Supporting Case Law' },
  { value: 'Applicable Supporting Scientific Literature', text: 'Applicable Supporting Scientific Literature' },
  { value: 'Automated Wetland Datasheet', text: 'Automated Wetland Datasheet (excel file)' },
  { value: 'Aquatic Resource Delineation Report', text: 'Aquatic Resource Delineation Report' },
  { value: 'FEMA FIRM Maps', text: 'FEMA/FIRM Maps' },
  {
    value: 'Historic Properties Cultural Resources Information',
    text: 'Historic Properties/Cultural Resources Information',
  },
  { value: 'Maps Plans Plots or Plats', text: 'Maps, Plans, Plots or Plats' },
  { value: 'Mitigation Plan', text: 'Mitigation Plan' },
  { value: 'National Wetland Inventory Map', text: 'National Wetland Inventory Map' },
  { value: 'NWP 27 Report', text: 'NWP 27 Report' },
  { value: 'OHWM Datasheets', text: 'OHWM Datasheets' },
  { value: 'Other Information', text: 'Other Information' },
  { value: 'Project Plans', text: 'Project Plans' },
  { value: 'Site Photographs', text: 'Site Photograph(s)' },
  { value: 'State Water Quality Certification', text: 'State Water Quality Certification' },
  { value: 'State Local Aquatic Resource form', text: 'State/Local Aquatic Resource form' },
  { value: 'State Local Wetland Inventory Map', text: 'State/Local Wetland Inventory Map' },
  { value: 'Threatened Endangered Species Report', text: 'Threatened & Endangered Species Report' },
  { value: 'US Geological Survey Map', text: 'U.S. Geological Survey Map' },
  { value: 'USACE Navigable Waters Study', text: 'USACE Navigable Waters Study' },
  { value: 'USDS NRCS Soil Survey Map', text: 'USDS NRCS Soil Survey Map' },
  { value: 'USGS Hydrologic Unit Code Map', text: 'USGS Hydrologic Unit Code Map' },
  { value: 'USGS National Hydrography Dataset Map', text: 'USGS National Hydrography Dataset Map' },
  { value: 'Wetland Datasheets', text: 'Wetland Datasheets' },
].sort((a, b) => a.text.localeCompare(b.text));

// NOTE: make sure any special characters are removed from the values
export const preAppDocTypes = [
  { value: 'Aquatic Resource Delineation Report', text: 'Aquatic Resource Delineation Report' },
  { value: 'Draft Alternative Analysis', text: 'Draft Alternative Analysis' },
  {
    value: 'Draft Avoidance and Minimization Statement and or Conceptual Compensatory Mitigation Plan',
    text: 'Draft Avoidance and Minimization Statement and/or Conceptual Compensatory Mitigation Plan',
  },
  { value: 'Draft Prospectus Conceptual Mitigation Plan', text: 'Draft Prospectus/Conceptual Mitigation Plan' },
  { value: 'Other Information', text: 'Other Information' },
  { value: 'Scoping Letter', text: 'Scoping Letter' },
  { value: 'Threatened Endangered Species Report', text: 'Threatened & Endangered Species Report' },
].sort((a, b) => a.text.localeCompare(b.text));

// NOTE: make sure any special characters are removed from the values
export const violationComplaintDocTypes = [
  { value: 'Maps', text: 'Maps' },
  { value: 'Other Information', text: 'Other Information' },
  { value: 'Photographs', text: 'Photographs' },
  { value: 'Plans', text: 'Plans' },
].sort((a, b) => a.text.localeCompare(b.text));

export const requestReasons = [
  {
    value:
      'I intend to construct/develop a project or perform activities on this parcel which would be designed to avoid all aquatic resources.',
    label:
      'I intend to construct/develop a project or perform activities on this parcel which would be designed to avoid all aquatic resources',
  },
  {
    value:
      'I intend to construct/develop a project or perform activities on this parcel which would be designed to avoid all jurisdictional aquatic resources under USACE authority.',
    label:
      'I intend to construct/develop a project or perform activities on this parcel which would be designed to avoid all jurisdictional aquatic resources under USACE authority',
  },
  {
    value:
      'I intend to construct/develop a project or perform activities on this parcel which may require authorization from the USACE, and the JD would be used to avoid and minimize impacts to jurisdictional aquatic resources and as an initial step in a future permitting process.',
    label:
      'I intend to construct/develop a project or perform activities on this parcel which may require authorization from the USACE; and the JD would be used to avoid and minimize impacts to jurisdictional aquatic resources and as an initial step in a future permitting process',
  },
  {
    value:
      'I intend to construct/develop a project or perform activities in a navigable water of the U.S. which is included on the district Section 10 list and/or is subject to the ebb and flow of the tide.',
    label:
      'I intend to construct/develop a project or perform activities in a navigable water of the U.S. which is included on the district Section 10 list and/ or is subject to the ebb and flow of the tide',
  },
  {
    value: 'A USACE JD is required in order to obtain my local/state authorization.',
    label: 'A USACE Jurisdictional Determination is required in order to obtain my local/state authorization.',
  },
  {
    value:
      'I intend to contest jurisdiction over a particular aquatic resource and request the USACE confirm that jurisdiction does/does not exist over the aquatic resource on the parcel.',
    label:
      'I intend to contest jurisdiction over a particular aquatic resource and request the USACE confirm that jurisdiction does/does not exist over the aquatic resource on the parcel',
  },
  {
    value: 'I believe that the site may be comprised entirely of dry land.',
    label: 'I believe that the site may be comprised entirely of dry land',
  },
  { value: 'Other', label: 'Other (e.g. verify delineation report)' },
];

export const countries = [
  { value: 'US', text: 'United States of America' },
  { value: 'AF', text: 'Afghanistan' },
  { value: 'AX', text: 'Aland Islands' },
  { value: 'AL', text: 'Albania' },
  { value: 'DZ', text: 'Algeria' },
  { value: 'AS', text: 'American Samoa' },
  { value: 'AD', text: 'Andorra' },
  { value: 'AO', text: 'Angola' },
  { value: 'AI', text: 'Anguilla' },
  { value: 'AQ', text: 'Antarctica' },
  { value: 'AG', text: 'Antigua and Barbuda' },
  { value: 'AR', text: 'Argentina' },
  { value: 'AM', text: 'Armenia' },
  { value: 'AW', text: 'Aruba' },
  { value: 'AU', text: 'Australia' },
  { value: 'AT', text: 'Austria' },
  { value: 'AZ', text: 'Azerbaijan' },
  { value: 'BS', text: 'Bahamas' },
  { value: 'BH', text: 'Bahrain' },
  { value: 'BD', text: 'Bangladesh' },
  { value: 'BB', text: 'Barbados' },
  { value: 'BY', text: 'Belarus' },
  { value: 'BE', text: 'Belgium' },
  { value: 'BZ', text: 'Belize' },
  { value: 'BJ', text: 'Benin' },
  { value: 'BM', text: 'Bermuda' },
  { value: 'BT', text: 'Bhutan' },
  { value: 'BO', text: 'Bolivia (Plurinational State of)' },
  { value: 'BQ', text: 'Bonaire, Sint Eustatius and Saba' },
  { value: 'BA', text: 'Bosnia and Herzegovina' },
  { value: 'BW', text: 'Botswana' },
  { value: 'BV', text: 'Bouvet Island' },
  { value: 'BR', text: 'Brazil' },
  { value: 'IO', text: 'British Indian Ocean Territory' },
  { value: 'BN', text: 'Brunei Darussalam' },
  { value: 'BG', text: 'Bulgaria' },
  { value: 'BF', text: 'Burkina Faso' },
  { value: 'BI', text: 'Burundi' },
  { value: 'CV', text: 'Cabo Verde' },
  { value: 'KH', text: 'Cambodia' },
  { value: 'CM', text: 'Cameroon' },
  { value: 'CA', text: 'Canada' },
  { value: 'KY', text: 'Cayman Islands' },
  { value: 'CF', text: 'Central African Republic' },
  { value: 'TD', text: 'Chad' },
  { value: 'CL', text: 'Chile' },
  { value: 'CN', text: 'China' },
  { value: 'CX', text: 'Christmas Island' },
  { value: 'CC', text: 'Cocos (Keeling) Islands' },
  { value: 'CO', text: 'Colombia' },
  { value: 'KM', text: 'Comoros' },
  { value: 'CG', text: 'Congo' },
  { value: 'CD', text: 'Congo (Democratic Republic of the)' },
  { value: 'CK', text: 'Cook Islands' },
  { value: 'CR', text: 'Costa Rica' },
  { value: 'CI', text: "Cote d'Ivoire" },
  { value: 'HR', text: 'Croatia' },
  { value: 'CU', text: 'Cuba' },
  { value: 'CW', text: 'Curacao' },
  { value: 'CY', text: 'Cyprus' },
  { value: 'CZ', text: 'Czech Republic' },
  { value: 'DK', text: 'Denmark' },
  { value: 'DJ', text: 'Djibouti' },
  { value: 'DM', text: 'Dominica' },
  { value: 'DO', text: 'Dominican Republic' },
  { value: 'EC', text: 'Ecuador' },
  { value: 'EG', text: 'Egypt' },
  { value: 'SV', text: 'El Salvador' },
  { value: 'GQ', text: 'Equatorial Guinea' },
  { value: 'ER', text: 'Eritrea' },
  { value: 'EE', text: 'Estonia' },
  { value: 'ET', text: 'Ethiopia' },
  { value: 'FK', text: 'Falkland Islands (Malvinas)' },
  { value: 'FO', text: 'Faroe Islands' },
  { value: 'FJ', text: 'Fiji' },
  { value: 'FI', text: 'Finland' },
  { value: 'FR', text: 'France' },
  { value: 'GF', text: 'French Guiana' },
  { value: 'PF', text: 'French Polynesia' },
  { value: 'TF', text: 'French Southern Territories' },
  { value: 'GA', text: 'Gabon' },
  { value: 'GM', text: 'Gambia' },
  { value: 'GE', text: 'Georgia' },
  { value: 'DE', text: 'Germany' },
  { value: 'GH', text: 'Ghana' },
  { value: 'GI', text: 'Gibraltar' },
  { value: 'GR', text: 'Greece' },
  { value: 'GL', text: 'Greenland' },
  { value: 'GD', text: 'Grenada' },
  { value: 'GP', text: 'Guadeloupe' },
  { value: 'GU', text: 'Guam' },
  { value: 'GT', text: 'Guatemala' },
  { value: 'GG', text: 'Guernsey' },
  { value: 'GN', text: 'Guinea' },
  { value: 'GW', text: 'Guinea-Bissau' },
  { value: 'GY', text: 'Guyana' },
  { value: 'HT', text: 'Haiti' },
  { value: 'HM', text: 'Heard Island and McDonald Islands' },
  { value: 'VA', text: 'Holy See' },
  { value: 'HN', text: 'Honduras' },
  { value: 'HK', text: 'Hong Kong' },
  { value: 'HU', text: 'Hungary' },
  { value: 'IS', text: 'Iceland' },
  { value: 'IN', text: 'India' },
  { value: 'ID', text: 'Indonesia' },
  { value: 'IR', text: 'Iran (Islamic Republic of)' },
  { value: 'IQ', text: 'Iraq' },
  { value: 'IE', text: 'Ireland' },
  { value: 'IM', text: 'Isle of Man' },
  { value: 'IL', text: 'Israel' },
  { value: 'IT', text: 'Italy' },
  { value: 'JM', text: 'Jamaica' },
  { value: 'JP', text: 'Japan' },
  { value: 'JE', text: 'Jersey' },
  { value: 'JO', text: 'Jordan' },
  { value: 'KZ', text: 'Kazakhstan' },
  { value: 'KE', text: 'Kenya' },
  { value: 'KI', text: 'Kiribati' },
  { value: 'KP', text: "Korea (Democratic People's Republic of)" },
  { value: 'KR', text: 'Korea (Republic of)' },
  { value: 'KW', text: 'Kuwait' },
  { value: 'KG', text: 'Kyrgyzstan' },
  { value: 'LA', text: "Lao People's Democratic Republic" },
  { value: 'LV', text: 'Latvia' },
  { value: 'LB', text: 'Lebanon' },
  { value: 'LS', text: 'Lesotho' },
  { value: 'LR', text: 'Liberia' },
  { value: 'LY', text: 'Libya' },
  { value: 'LI', text: 'Liechtenstein' },
  { value: 'LT', text: 'Lithuania' },
  { value: 'LU', text: 'Luxembourg' },
  { value: 'MO', text: 'Macao' },
  { value: 'MK', text: 'Macedonia (the former Yugoslav Republic of)' },
  { value: 'MG', text: 'Madagascar' },
  { value: 'MW', text: 'Malawi' },
  { value: 'MY', text: 'Malaysia' },
  { value: 'MV', text: 'Maldives' },
  { value: 'ML', text: 'Mali' },
  { value: 'MT', text: 'Malta' },
  { value: 'MH', text: 'Marshall Islands' },
  { value: 'MQ', text: 'Martinique' },
  { value: 'MR', text: 'Mauritania' },
  { value: 'MU', text: 'Mauritius' },
  { value: 'YT', text: 'Mayotte' },
  { value: 'MX', text: 'Mexico' },
  { value: 'FM', text: 'Micronesia (Federated States of)' },
  { value: 'MD', text: 'Moldova (Republic of)' },
  { value: 'MC', text: 'Monaco' },
  { value: 'MN', text: 'Mongolia' },
  { value: 'ME', text: 'Montenegro' },
  { value: 'MS', text: 'Montserrat' },
  { value: 'MA', text: 'Morocco' },
  { value: 'MZ', text: 'Mozambique' },
  { value: 'MM', text: 'Myanmar' },
  { value: 'NA', text: 'Namibia' },
  { value: 'NR', text: 'Nauru' },
  { value: 'NP', text: 'Nepal' },
  { value: 'NL', text: 'Netherlands' },
  { value: 'NC', text: 'New Caledonia' },
  { value: 'NZ', text: 'New Zealand' },
  { value: 'NI', text: 'Nicaragua' },
  { value: 'NE', text: 'Niger' },
  { value: 'NG', text: 'Nigeria' },
  { value: 'NU', text: 'Niue' },
  { value: 'NF', text: 'Norfolk Island' },
  { value: 'MP', text: 'Northern Mariana Islands' },
  { value: 'NO', text: 'Norway' },
  { value: 'OM', text: 'Oman' },
  { value: 'PK', text: 'Pakistan' },
  { value: 'PW', text: 'Palau' },
  { value: 'PS', text: 'Palestine, State of' },
  { value: 'PA', text: 'Panama' },
  { value: 'PG', text: 'Papua New Guinea' },
  { value: 'PY', text: 'Paraguay' },
  { value: 'PE', text: 'Peru' },
  { value: 'PH', text: 'Philippines' },
  { value: 'PN', text: 'Pitcairn' },
  { value: 'PL', text: 'Poland' },
  { value: 'PT', text: 'Portugal' },
  { value: 'PR', text: 'Puerto Rico' },
  { value: 'QA', text: 'Qatar' },
  { value: 'RE', text: 'Reunion' },
  { value: 'RO', text: 'Romania' },
  { value: 'RU', text: 'Russian Federation' },
  { value: 'RW', text: 'Rwanda' },
  { value: 'BL', text: 'Saint Barthélemy' },
  { value: 'SH', text: 'Saint Helena, Ascension and Tristan da Cunha' },
  { value: 'KN', text: 'Saint Kitts and Nevis' },
  { value: 'LC', text: 'Saint Lucia' },
  { value: 'MF', text: 'Saint Martin (French part)' },
  { value: 'PM', text: 'Saint Pierre and Miquelon' },
  { value: 'VC', text: 'Saint Vincent and the Grenadines' },
  { value: 'WS', text: 'Samoa' },
  { value: 'SM', text: 'San Marino' },
  { value: 'ST', text: 'Sao Tome and Principe' },
  { value: 'SA', text: 'Saudi Arabia' },
  { value: 'SN', text: 'Senegal' },
  { value: 'RS', text: 'Serbia' },
  { value: 'SC', text: 'Seychelles' },
  { value: 'SL', text: 'Sierra Leone' },
  { value: 'SG', text: 'Singapore' },
  { value: 'SX', text: 'Sint Maarten (Dutch part)' },
  { value: 'SK', text: 'Slovakia' },
  { value: 'SI', text: 'Slovenia' },
  { value: 'SB', text: 'Solomon Islands' },
  { value: 'SO', text: 'Somalia' },
  { value: 'ZA', text: 'South Africa' },
  { value: 'GS', text: 'South Georgia and the South Sandwich Islands' },
  { value: 'SS', text: 'South Sudan' },
  { value: 'ES', text: 'Spain' },
  { value: 'LK', text: 'Sri Lanka' },
  { value: 'SD', text: 'Sudan' },
  { value: 'SR', text: 'Suriname' },
  { value: 'SJ', text: 'Svalbard and Jan Mayen' },
  { value: 'SZ', text: 'Swaziland' },
  { value: 'SE', text: 'Sweden' },
  { value: 'CH', text: 'Switzerland' },
  { value: 'SY', text: 'Syrian Arab Republic' },
  { value: 'TW', text: 'Taiwan, Province of China' },
  { value: 'TJ', text: 'Tajikistan' },
  { value: 'TZ', text: 'Tanzania, United Republic of' },
  { value: 'TH', text: 'Thailand' },
  { value: 'TL', text: 'Timor-Leste' },
  { value: 'TG', text: 'Togo' },
  { value: 'TK', text: 'Tokelau' },
  { value: 'TO', text: 'Tonga' },
  { value: 'TT', text: 'Trinidad and Tobago' },
  { value: 'TN', text: 'Tunisia' },
  { value: 'TR', text: 'Turkey' },
  { value: 'TM', text: 'Turkmenistan' },
  { value: 'TC', text: 'Turks and Caicos Islands' },
  { value: 'TV', text: 'Tuvalu' },
  { value: 'UG', text: 'Uganda' },
  { value: 'UA', text: 'Ukraine' },
  { value: 'AE', text: 'United Arab Emirates' },
  { value: 'GB', text: 'United Kingdom of Great Britain and Northern Ireland' },
  { value: 'UY', text: 'Uruguay' },
  { value: 'UZ', text: 'Uzbekistan' },
  { value: 'VU', text: 'Vanuatu' },
  { value: 'VE', text: 'Venezuela (Bolivarian Republic of)' },
  { value: 'VN', text: 'Viet Nam' },
  { value: 'WF', text: 'Wallis and Futuna' },
  { value: 'EH', text: 'Western Sahara' },
  { value: 'YE', text: 'Yemen' },
  { value: 'ZM', text: 'Zambia' },
  { value: 'ZW', text: 'Zimbabwe' },
];

// create key value array for quickly getting full country name from alpha2 code (e.g. countryKeys['US'] => 'United States of America')
export const countryKeys = countries.reduce((r, { text, value }) => {
  r[value] = text;
  return r;
}, {});

export const countriesAlpha3Alpha2Lookup = {
  AFG: 'AF',
  ALB: 'AL',
  DZA: 'DZ',
  ASM: 'AS',
  AND: 'AD',
  AGO: 'AO',
  AIA: 'AI',
  ATA: 'AQ',
  ATG: 'AG',
  ARG: 'AR',
  ARM: 'AM',
  ABW: 'AW',
  AUS: 'AU',
  AUT: 'AT',
  AZE: 'AZ',
  BHS: 'BS',
  BHR: 'BH',
  BGD: 'BD',
  BRB: 'BB',
  BLR: 'BY',
  BEL: 'BE',
  BLZ: 'BZ',
  BEN: 'BJ',
  BMU: 'BM',
  BTN: 'BT',
  BOL: 'BO',
  BIH: 'BA',
  BWA: 'BW',
  BVT: 'BV',
  BRA: 'BR',
  IOT: 'IO',
  BRN: 'BN',
  BGR: 'BG',
  BFA: 'BF',
  BDI: 'BI',
  CPV: 'CV',
  KHM: 'KH',
  CMR: 'CM',
  CAN: 'CA',
  CYM: 'KY',
  CAF: 'CF',
  TCD: 'TD',
  CHL: 'CL',
  CHN: 'CN',
  CXR: 'CX',
  CCK: 'CC',
  COL: 'CO',
  COM: 'KM',
  COG: 'CG',
  COD: 'CD',
  COK: 'CK',
  CRI: 'CR',
  CIV: 'CI',
  HRV: 'HR',
  CUB: 'CU',
  CYP: 'CY',
  CZE: 'CZ',
  DNK: 'DK',
  DJI: 'DJ',
  DMA: 'DM',
  DOM: 'DO',
  ECU: 'EC',
  EGY: 'EG',
  SLV: 'SV',
  GNQ: 'GQ',
  ERI: 'ER',
  EST: 'EE',
  SWZ: 'SZ',
  ETH: 'ET',
  FLK: 'FK',
  FRO: 'FO',
  FJI: 'FJ',
  FIN: 'FI',
  FRA: 'FR',
  GUF: 'GF',
  PYF: 'PF',
  ATF: 'TF',
  GAB: 'GA',
  GMB: 'GM',
  GEO: 'GE',
  DEU: 'DE',
  GHA: 'GH',
  GIB: 'GI',
  GRC: 'GR',
  GRL: 'GL',
  GRD: 'GD',
  GLP: 'GP',
  GUM: 'GU',
  GTM: 'GT',
  GIN: 'GN',
  GNB: 'GW',
  GUY: 'GY',
  HTI: 'HT',
  HMD: 'HM',
  VAT: 'VA',
  HND: 'HN',
  HKG: 'HK',
  HUN: 'HU',
  ISL: 'IS',
  IND: 'IN',
  IDN: 'ID',
  IRN: 'IR',
  IRQ: 'IQ',
  IRL: 'IE',
  ISR: 'IL',
  ITA: 'IT',
  JAM: 'JM',
  JPN: 'JP',
  JOR: 'JO',
  KAZ: 'KZ',
  KEN: 'KE',
  KIR: 'KI',
  PRK: 'KP',
  KOR: 'KR',
  KWT: 'KW',
  KGZ: 'KG',
  LAO: 'LA',
  LVA: 'LV',
  LBN: 'LB',
  LSO: 'LS',
  LBR: 'LR',
  LBY: 'LY',
  LIE: 'LI',
  LTU: 'LT',
  LUX: 'LU',
  MAC: 'MO',
  MDG: 'MG',
  MWI: 'MW',
  MYS: 'MY',
  MDV: 'MV',
  MLI: 'ML',
  MLT: 'MT',
  MHL: 'MH',
  MTQ: 'MQ',
  MRT: 'MR',
  MUS: 'MU',
  MYT: 'YT',
  MEX: 'MX',
  FSM: 'FM',
  MDA: 'MD',
  MCO: 'MC',
  MNG: 'MN',
  MNE: 'ME',
  MSR: 'MS',
  MAR: 'MA',
  MOZ: 'MZ',
  MMR: 'MM',
  NAM: 'NA',
  NRU: 'NR',
  NPL: 'NP',
  NLD: 'NL',
  NCL: 'NC',
  NZL: 'NZ',
  NIC: 'NI',
  NER: 'NE',
  NGA: 'NG',
  NIU: 'NU',
  NFK: 'NF',
  MKD: 'MK',
  MNP: 'MP',
  NOR: 'NO',
  OMN: 'OM',
  PAK: 'PK',
  PLW: 'PW',
  PSE: 'PS',
  PAN: 'PA',
  PNG: 'PG',
  PRY: 'PY',
  PER: 'PE',
  PHL: 'PH',
  PCN: 'PN',
  POL: 'PL',
  PRT: 'PT',
  PRI: 'PR',
  QAT: 'QA',
  REU: 'RE',
  ROU: 'RO',
  RUS: 'RU',
  RWA: 'RW',
  SHN: 'SH',
  KNA: 'KN',
  LCA: 'LC',
  VCT: 'VC',
  WSM: 'WS',
  SMR: 'SM',
  STP: 'ST',
  SAU: 'SA',
  SEN: 'SN',
  SRB: 'RS',
  SYC: 'SC',
  SLE: 'SL',
  SGP: 'SG',
  SXM: 'SX',
  SVK: 'SK',
  SVN: 'SI',
  SLB: 'SB',
  SOM: 'SO',
  ZAF: 'ZA',
  SGS: 'GS',
  SSD: 'SS',
  ESP: 'ES',
  LKA: 'LK',
  SDN: 'SD',
  SUR: 'SR',
  SJM: 'SJ',
  SWE: 'SE',
  CHE: 'CH',
  SYR: 'SY',
  TWN: 'TW',
  TJK: 'TJ',
  TZA: 'TZ',
  THA: 'TH',
  TLS: 'TL',
  TGO: 'TG',
  TKL: 'TK',
  TON: 'TO',
  TTO: 'TT',
  TUN: 'TN',
  TUR: 'TR',
  TKM: 'TM',
  TCA: 'TC',
  TUV: 'TV',
  UGA: 'UG',
  UKR: 'UA',
  ARE: 'AE',
  GBR: 'GB',
  USA: 'US',
  URY: 'UY',
  UZB: 'UZ',
  VUT: 'VU',
  VEN: 'VE',
  VNM: 'VN',
  VGB: 'VG',
  VIR: 'VI',
  WLF: 'WF',
  ESH: 'EH',
  YEM: 'YE',
  ZMB: 'ZM',
  ZWE: 'ZW',
};

export const projectLocationCountries = [
  {
    text: 'United States of America',
    value: 'US',
  },
  {
    text: 'American Samoa',
    value: 'AS',
  },
  {
    text: 'Commonwealth of the Northern Mariana Islands',
    value: 'MP',
  },
  {
    text: 'Guam',
    value: 'GU',
  },
  {
    text: 'Puerto Rico',
    value: 'PR',
  },
  {
    text: 'United States Virgin Islands',
    value: 'VI',
  },
];

//to make multi select look like other elements
export const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    border: '1px solid #e74c3c',
    // padding: '0.375rem 1.5rem 0.375rem 0.75rem',
    paddingRight: '0.80rem',
    backgroundImage:
      'url("data:image/svg+xml,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2712%27 height=%2712%27 fill=%27none%27 stroke=%27%23e74c3c%27 viewBox=%270 0 12 12%27%3e%3ccircle cx=%276%27 cy=%276%27 r=%274.5%27/%3e%3cpath stroke-linejoin=%27round%27 d=%27M5.8 3.6h.4L6 6.5z%27/%3e%3ccircle cx=%276%27 cy=%278.2%27 r=%27.6%27 fill=%27%23e74c3c%27 stroke=%27none%27/%3e%3c/svg%3e")',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 0.375rem center',
    backgroundSize: '1rem 1rem',
    borderColor: state.isFocused ? '#ccc' : '#e74c3c',
    boxShadow: state.isFocused ? '0 0 0 2px #e74c3c' : 'none',
    '&:hover': {
      borderColor: state.isFocused ? '#ccc' : '#e74c3c',
    },
  }),
};

export const customSelectStylesNonRequired = {
  control: (base) => ({
    ...base,
  }),
};

const resourceMapUseTipsList = [
  {
    icon: mdiPlus,
    label: 'Zoom In',
    text: 'Click the plus sign to zoom in on the map. You can also click one in the map and use the scroll wheel on your mouse to zoom in and out. With the focus of your cursor on the map, you can pan the map with the hand icon',
    type: 'icon',
  },
  {
    icon: mdiMinus,
    label: 'Zoom Out',
    text: 'Click the minus sign to zoom out on the map. See above for further map navigation tips',
    type: 'icon',
  },
  {
    icon: fullscreenSVG,
    label: 'Full Screen',
    text: 'Click the full screen icon to zoom out to the original extent of the map',
    type: 'svg',
  },
  {
    icon: mdiFolderUploadOutline,
    label: 'Upload GDB File',
    text: 'Click this icon to upload the project location information from a geodatabase (GDB) instead of digitizing the location on the map. The geodatabase must be compressed (.zip or .gdb)',
    type: 'icon',
  },
  {
    icon: polyLineSVG,
    label: 'Add a Line Feature',
    text: 'Select the line tool to add a project location as a line. Click the starting point location and any interim shape points. Double-click the final shape point of the line or click to set the location of the final shape point and then select "Finish" from the popup menu in the map',
    type: 'svg',
  },
  {
    icon: polygonSVG,
    label: 'Add a Polygon Feature',
    text: 'Select the polygon tool to add a project location as a polygon. Click the starting point location followed by the remaining shape points. When complete, either double-click the last shape point or click the set the location of the final shape point and then select "Finish" from the popup menu in the map',
    type: 'svg',
  },
  {
    icon: mdiMapMarker,
    label: 'Add a Point Feature',
    text: 'Select the point tool to add a project location as a single point. Click the location of the point',
    type: 'icon',
  },
  {
    icon: mdiTrashCanOutline,
    label: 'Delete Features',
    text: 'Select this option to delete the location information from the map. You will then need to re-create the project location. After clicking the trash can icon, select Finish, Cancel, or Remove All to delete the location entered on the map',
    type: 'icon',
  },
  {
    icon: mdiMagnify,
    label: 'Search',
    text: 'Use this component to search for a location via text (address, city, point of interest etc.)',
    type: 'icon',
  },
  {
    icon: searchMarker,
    label: 'Search Marker',
    text: 'This marker is used to indicate the location of the search result',
    type: 'img',
  },
  {
    icon: mdiClose,
    label: 'Reset Map',
    text: 'Use this icon to reset the map to its original state after using the search component. This icon is only visible when the "Search Marker" is present',
    type: 'icon',
  },
  {
    icon: mdiLayersTriple,
    label: 'Layers',
    text: 'Mouse over the layers icon to view the available base maps. The base map can either be viewed as a street map, aerial imagery, or topographic map',
    type: 'icon',
  },
];

export const generateMapUseTipsAccordionList = (mapType) => {
  const mapUseTipsList = mapType === 'resource' ? resourceMapUseTipsList : PNmapUseTipsList;
  return [
    {
      title: 'Map Use Tips',
      content: (
        <>
          {mapUseTipsList.map((item, index) => (
            <p key={index} className={`margin-bottom-0 ${index !== 0 ? 'margin-top-105' : ''}`}>
              {item.type === 'svg' ? (
                <>
                  <span>{item.icon}</span> {item.label} - {item.text}.
                </>
              ) : item.type === 'img' ? (
                <>
                  <span>
                    <img src={item.icon} alt='' width='24' height='24' />
                  </span>{' '}
                  {item.label} - {item.text}.
                </>
              ) : (
                <>
                  <span>
                    <Icon path={item.icon} size='24px' />
                  </span>{' '}
                  {item.label} - {item.text}.
                </>
              )}
            </p>
          ))}
        </>
      ),
      id: '1',
      headingLevel: 'h3',
    },
  ];
};

export const applyConditionalCleanup = ({
  payload,
  requestSectionKey,
  toggleFieldName,
  contactType,
  requestFieldNamesToClear = [],
  requestArrayFieldsToClear = [],
}) => {
  const toggleValue = payload?.request?.[requestSectionKey]?.[0]?.[toggleFieldName];
  if (toggleValue === false || toggleValue === 'false') {
    const nextRequest = {
      ...payload.request,
      ...(payload?.request?.contacts &&
        contactType && {
          contacts: payload.request.contacts.filter((contact) => contact?.contactType !== contactType),
        }),
    };
    requestFieldNamesToClear.forEach((fieldName) => {
      nextRequest[fieldName] = null;
    });
    requestArrayFieldsToClear.forEach((fieldName) => {
      nextRequest[fieldName] = [];
    });
    return {
      ...payload,
      request: nextRequest,
    };
  }
  return payload;
};

export const formatPreAppAnswerSet = (payload) =>
  applyConditionalCleanup({
    payload,
    requestSectionKey: 'preApplications',
    toggleFieldName: 'hasAddPropertyOwners',
    contactType: ContactTypes.PropertyOwner,
  });

export const applyFormatCleanup = ({ payload, requestSectionKey }) => {
  const payloadWithAgentCleanup = applyConditionalCleanup({
    payload,
    requestSectionKey,
    toggleFieldName: 'hasHiredAgent',
    contactType: ContactTypes.Agent,
    requestFieldNamesToClear: ['agentAuthComments', 'ownerEntryComments'],
  });

  const hasPreviousFileNumber = payloadWithAgentCleanup?.request?.hasPreviousFileNumber;
  if (hasPreviousFileNumber === false || hasPreviousFileNumber === 'false') {
    return {
      ...payloadWithAgentCleanup,
      request: {
        ...payloadWithAgentCleanup.request,
        previousFileNumbers: [],
      },
    };
  }
  return payloadWithAgentCleanup;
};

export const saveRequest = async (isSaveButton = false, uploadRequestFiles, updateRequestFn) => {
  const { answerSet, removeDeletedTableAnswers } = useJpa.getState();
  const { resetDirtySections, isAnySectionDirty, selectedRequestId } = useRequest.getState();
  const { setIsLoading, setIsSavingProgress } = useApp.getState();
  const { hasTableInputChanges } = useTableInput.getState();

  const store = getAppStore();

  if (!isSaveButton && store.selectIsAnonymous() === true) {
    return;
  }

  const requestFormData = store.selectRequestFormData();
  const request = requestFormData?.request;
  const reqTypeId = Number(selectedRequestId);
  const isArRequest = [RequestTypeIds.AquaticResourceDelineationReport, RequestTypeIds.PermitApplication].includes(
    reqTypeId
  );

  const arTableId = `aquatic-resources-table-${request.requestID}`;
  const hasArChanges = hasTableInputChanges(arTableId);

  if (!isSaveButton && !isAnySectionDirty() && !hasArChanges) {
    return;
  }

  const arChanges = isArRequest ? convertArTableInputDataToApiData(request.requestID, request.version) : null;
  const payload = filterNullEmptyObjects(requestFormData);
  const requestType = requestFormData?.request?.requestType;
  const formVersion = requestFormData?.request?.formVersion;
  const hasSupportingDocuments = requestFormData?.request?.[RequestMap[reqTypeId]]?.[0]?.hasSupportingDocuments;

  // Reset Supporting Documents
  if (hasSupportingDocuments === 'false') {
    store.doResetFileSection('Supporting Documents', true);
  }

  const nprCfg = resolveNprConfigForRequest({
    requestType,
    formVersion: formVersion,
  });

  const updatePayload = {
    [RequestTypeIds.ApplicationForDAPermit]: () =>
      applyFormatCleanup({
        payload: formatJpaRequestAnswerSet(payload, answerSet),
        requestSectionKey: 'permits',
      }),

    [RequestTypeIds.PreconstructionNotification]: () =>
      applyFormatCleanup({
        payload: formatJpaRequestAnswerSet(payload, answerSet),
        requestSectionKey: 'permits',
      }),

    [RequestTypeIds.NoPermitRequired]: () =>
      applyFormatCleanup({
        payload: formatNprAnswerSet(payload),
        requestSectionKey: 'noPermitRequireds',
      }),

    [RequestTypeIds.ActivityBasedNoPermitRequired]: () =>
      applyFormatCleanup({
        payload: formatNprAnswerSet(payload, {
          questionsById: nprCfg.questionsById,
          nprQuestionIdByName: nprCfg.nprQuestionIdByName,
        }),
        requestSectionKey: 'noPermitRequireds',
      }),

    [RequestTypeIds.NonPreconstructionNotification]: () =>
      applyFormatCleanup({
        payload: formatNonPcnAnswerSet(payload),
        requestSectionKey: 'nonPCNRequests',
      }),

    [RequestTypeIds.PermitApplication]: () =>
      applyFormatCleanup({
        payload: formatJpaRequestAnswerSet(formatPermitApplicationAnswers(payload), answerSet),
        requestSectionKey: 'permits',
      }),

    [RequestTypeIds.JurisdictionalDetermination]: () =>
      applyFormatCleanup({
        payload: formatJdPayload(payload),
        requestSectionKey: 'jdRequests',
      }),

    [RequestTypeIds.AquaticResourceDelineationReport]: () =>
      applyFormatCleanup({
        payload: formatJdPayload(payload),
        requestSectionKey: 'jdRequests',
      }),

    [RequestTypeIds.PreApplication]: () =>
      applyFormatCleanup({
        payload: formatPreAppAnswerSet(payload),
        requestSectionKey: 'preApplications',
      }),

    [RequestTypeIds.ViolationComplaint]: () => formatVcPayload(payload),
  };

  const updateFns = {
    [RequestTypeIds.AquaticResourceDelineationReport]: store.doUploadAndUpdateJD,
    [RequestTypeIds.JurisdictionalDetermination]: store.doUploadAndUpdateJD,
    [RequestTypeIds.ApplicationForDAPermit]: store.doUploadAndUpdatePermits,
    [RequestTypeIds.PermitApplication]: store.doUploadAndUpdatePermits,
    [RequestTypeIds.PreconstructionNotification]: store.doUploadAndUpdatePermits,
    [RequestTypeIds.PreApplication]: store.doUploadAndUpdatePreApp,
    [RequestTypeIds.ViolationComplaint]: store.doUploadAndUpdateViolationComplaint,
    [RequestTypeIds.NoPermitRequired]: store.doUploadAndUpdateNpr,
    [RequestTypeIds.ActivityBasedNoPermitRequired]: store.doUploadAndUpdateNpr,
    [RequestTypeIds.NonPreconstructionNotification]: uploadRequestFiles,
  };

  if (hasArChanges) {
    const signed = Boolean(request?.isSubmit);

    setIsSavingProgress(true);
    setIsLoading(true, signed ? LoadingMessages.SubmitRequest : LoadingMessages.SaveRequest);

    try {
      await saveArChanges({
        arChanges,
        requestId: request.requestID,
      });
    } catch (error) {
      setIsSavingProgress(false);
      setIsLoading(false);
      throw error;
    }
  }

  const formatFn = updatePayload?.[reqTypeId];
  const apiPayload = formatFn ? formatFn(payload, answerSet) : payload;
  // @TODO: When other request types get converted, make logic more dynamic
  if (reqTypeId === RequestTypeIds.NonPreconstructionNotification) {
    store.selectValidFiles().length > 0
      ? updateFns[reqTypeId]({ data: apiPayload, updateFn: updateRequestFn })
      : updateRequestFn(apiPayload);
  } else {
    updateFns[reqTypeId](apiPayload, RequestStatus.Draft, {
      hasArChanges: Boolean(hasArChanges),
    });
  }

  removeDeletedTableAnswers();
  resetDirtySections();
};

export const filterValidARPoints = (resourceData) => {
  if (!resourceData?.features) {
    return [];
  }

  const validPoints = resourceData?.features
    .filter(
      (feature) =>
        feature.geometry?.type === 'Point' &&
        feature.geometry.coordinates.length === 2 &&
        feature.geometry.coordinates.every(
          (coord) => coord !== undefined && coord !== null && !isNaN(coord) && coord !== 0
        )
    )
    .map((feature) => {
      const [longitude, latitude] = feature.geometry.coordinates;
      return [formatCoordFlt(longitude), formatCoordFlt(latitude)];
    });

  return validPoints;
};

const cleanValue = (value) => value?.trim().replace(/°/g, '');

const generateMultiPointGeoJson = (data) => ({
  type: GeometryTypes.MultiPoint,
  coordinates: data?.map((item) => [parseFloat(item.Longitude), parseFloat(item.Latitude)]),
});

export const validateArCsvData = async (csvData, table) => {
  const { openSecondaryModal } = useModal.getState();
  const columns = table.getAllColumns();
  const columnHeaders = columns?.map((column) => column.columnDef?.header);
  const providedHeaders = [...new Set(csvData.flatMap((data) => Object.keys(data)))];
  const headersMatch = providedHeaders?.every((header) => columnHeaders.includes(header.trim()));
  const numbersValid = csvData?.map((feature) => {
    const latitude = cleanValue(feature?.Latitude);
    const longitude = cleanValue(feature?.Longitude);
    const measurementAmount = cleanValue(feature?.['Measurement Amount']);
    const isValidCoordinate =
      !isNaN(parseFloat(latitude)) && !isNaN(parseFloat(longitude)) && !isNaN(parseFloat(measurementAmount));
    return isValidCoordinate;
  });
  const isValidNumbers = !numbersValid?.includes(false);
  // Validate if CSV headers match expected
  if (!headersMatch) {
    openSecondaryModal(statusModal, { msg: ErrorMessages.InvalidHeaders });
  } else {
    // Validate coordinates and number values
    if (!isValidNumbers) {
      openSecondaryModal(statusModal, { msg: ErrorMessages.InvalidCoordinates });
      return false;
    }
    csvData.forEach((dataItem) => {
      columns.forEach((column) => {
        if (dataItem?.hasOwnProperty(column.columnDef?.header)) {
          let columnValue = cleanValue(dataItem[column.columnDef?.header]);
          // Map to column.id
          if (column.columnDef?.header && dataItem?.hasOwnProperty(column.columnDef?.header)) {
            if (['Latitude', 'Longitude', 'Measurement Amount'].includes(column.columnDef?.header)) {
              dataItem[column.columnDef?.header] = !isNaN(columnValue) ? columnValue : '';
            } else {
              dataItem[column.columnDef?.header] = columnValue;
            }
          }
        }
      });
    });
    // Validate USACE District
    const isValidDistrict = await AorQueries.isGeometryInValidDistrict(generateMultiPointGeoJson(csvData));
    if (isValidDistrict) {
      return csvData;
    }

    if (isValidDistrict === false) {
      openSecondaryModal(statusModal, {
        msg: ErrorMessages.InvalidDistrictCsv,
        status: 'CSV contains invalid coordinates',
      });
    }

    return false;
  }
};

export const formatState = (state) => {
  const stateAbbr = state?.toUpperCase();
  const fullStateName = stateNamesByCode[stateAbbr];

  if (fullStateName) return fullStateName;
  return state;
};

export const formatCountry = (country) => {
  const lowercaseCountry = country?.toLowerCase();

  const mappedCountry = countries?.find(
    (item) => item.value.toLowerCase() === lowercaseCountry || item.text.toLowerCase() === lowercaseCountry
  );
  if (mappedCountry) {
    return mappedCountry.value;
  }

  return null;
};

export const formatBooleanString = (str) => {
  if (str == null) return '';

  const val = String(str).trim().toLowerCase();
  const trueValues = ['true', '1', 'yes', 'y', 'on'];
  const falseValues = ['false', '0', 'no', 'n', 'off'];

  if (trueValues.includes(val)) return 'true';
  if (falseValues.includes(val)) return 'false';

  return '';
};

export const formatProperCase = (str) => {
  if (!str) return '';
  // Split on space, dash, slash, or open parenthesis
  return str
    .split(/[\s\-\/(]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .replace(/ ([\-\/(]) /g, '$1')
    .replace(/\((\w)/g, (match, p1) => '(' + p1.toUpperCase());
};

export const formatMeasurementUnit = (val) => {
  const valToUpper = val?.toUpperCase();

  const validSquareFeet = [
    'SQUARE FEET',
    'SQUARE FOOT',
    'SQUARE FT',
    'SQUARE FT.',
    'SQ FT',
    'SQ. FT.',
    'SQ FT.',
    'SQ_FT',
  ];
  const validAcres = ['ACRE', 'ACRES', 'ACREAGE'];
  const validCubicYards = ['CUBIC YARDS', 'YARDS'];

  if (validSquareFeet.includes(valToUpper)) return 'Square Feet';
  if (validAcres.includes(valToUpper)) return 'Acres';
  if (validCubicYards.includes(valToUpper)) return 'Cubic Yards';

  return '';
};

export const formatOptionString = (str, options, labelAsValue) => {
  const lowercaseStr = str?.toLowerCase() || '';
  const lowercaseValues = options.map((type) => type.value.toLowerCase());
  const lowercaseLabels = options.map((type) => (type.label ? type.label.toLowerCase() : type.text.toLowerCase()));
  const values = options.map((type) => type.value);

  const activityValueMap = Object.fromEntries(lowercaseValues.map((val, i) => [val, values[i]]));
  const activityLabelMap = Object.fromEntries(lowercaseLabels.map((val, i) => [val, values[i]]));

  return labelAsValue ? activityLabelMap[lowercaseStr] : activityValueMap[lowercaseStr] || '';
};

export const validateCSV = (rows, requiredFields) => {
  const errors = [];
  const errorIndices = [];

  rows.forEach((row, rowIndex) => {
    const rowErrors = [];

    Object.entries(row).forEach(([key, value]) => {
      const currentRow = rowIndex + 1;
      const lowercaseKey = key?.toLowerCase();
      const isDomesticAddress = row?.country?.toLowerCase() === 'us' || !row?.country;

      if (
        (!value && requiredFields?.[lowercaseKey] === true) ||
        (!value && requiredFields?.[lowercaseKey] === 'us' && isDomesticAddress)
      ) {
        rowErrors.push({
          rowIndex: currentRow,
          columnName: key,
          error: ErrorMessages.RequiredMissing,
        });
      } else if (!value) {
        //do nothing, field is optional
        return;
      } else if (lowercaseKey === 'address') {
        const address = addressRegex.test(value);
        if (!address) {
          rowErrors.push({
            rowIndex: currentRow,
            columnName: key,
            error: ErrorMessages.RestrictedChars,
          });
        }
      } else if (['address line 2', 'addresstwo'].includes(lowercaseKey)) {
        const address2 = addressTwoRegex.test(value);
        if (!address2) {
          rowErrors.push({
            rowIndex: currentRow,
            columnName: key,
            error: ErrorMessages.RestrictedChars,
          });
        }
      } else if (['email', 'emailaddress'].includes(lowercaseKey)) {
        const email = emailRegex.test(value);
        if (!email) {
          rowErrors.push({
            rowIndex: currentRow,
            columnName: key,
            error: ErrorMessages.InvalidEmail,
          });
        }
      } else if (lowercaseKey === 'salutation') {
        const salutation = formatOptionString(value, salutations, false);
        if (!salutation) {
          rowErrors.push({
            rowIndex: currentRow,
            columnName: key,
            error: ErrorMessages.InvalidValue,
          });
        }
      } else if (['phone one', 'phone two', 'fax phone', 'phoneone', 'phonetwo', 'faxphone'].includes(lowercaseKey)) {
        const phone = usPhoneRegex.test(value);
        const intlPhone = phoneRegex.test(value);
        if (!phone && !intlPhone) {
          rowErrors.push({
            rowIndex: currentRow,
            columnName: key,
            error: ErrorMessages.InvalidValue,
          });
        }
      } else if (['phone one type', 'phone two type', 'phoneonetype', 'phonetwotype'].includes(lowercaseKey)) {
        const phoneTypes = formatOptionString(value, phonetypes, false);
        if (!phoneTypes) {
          rowErrors.push({
            rowIndex: currentRow,
            columnName: key,
            error: ErrorMessages.InvalidValue,
          });
        }
      } else if (
        ['phone one extension', 'phone two extension', 'phoneoneextension', 'phonetwoextension'].includes(lowercaseKey)
      ) {
        const phoneExt = phoneExtRegex.test(value);
        if (!phoneExt) {
          rowErrors.push({
            rowIndex: currentRow,
            columnName: key,
            error: ErrorMessages.InvalidValue,
          });
        }
      } else if (
        [
          'phone one country code',
          'phone two country code',
          'fax country code',
          'phoneonecountrycode',
          'phonetwocountrycode',
          'faxcountrycode',
        ].includes(lowercaseKey)
      ) {
        const cntryCode = countryCodeRegex.test(value);
        if (!cntryCode) {
          rowErrors.push({
            rowIndex: currentRow,
            columnName: key,
            error: ErrorMessages.InvalidValue,
          });
        }
      } else if (lowercaseKey === 'city') {
        const city = cityRegex.test(value);
        if (!city) {
          rowErrors.push({
            rowIndex: currentRow,
            columnName: key,
            error: ErrorMessages.InvalidValue,
          });
        }
      } else if (lowercaseKey === 'state') {
        const state = isDomesticAddress ? formatState(value) : value;
        if (!state) {
          rowErrors.push({
            rowIndex: currentRow,
            columnName: key,
            error: ErrorMessages.InvalidValue,
          });
        }
      } else if (lowercaseKey === 'country') {
        const country = formatCountry(value);
        if (!country) {
          rowErrors.push({
            rowIndex: currentRow,
            columnName: key,
            error: ErrorMessages.InvalidValue,
          });
        }
      } else if (['zip code', 'zipcode'].includes(lowercaseKey)) {
        const zipcode = isDomesticAddress ? zipCodeRegex.test(value) : value;
        if (!zipcode) {
          rowErrors.push({
            rowIndex: currentRow,
            columnName: key,
            error: ErrorMessages.InvalidValue,
          });
        }
      } else if (lowercaseKey === 'parcels') {
        const parcels = parcelsRegex.test(value);
        if (!parcels) {
          rowErrors.push({
            rowIndex: currentRow,
            columnName: key,
            error: ErrorMessages.InvalidValue,
          });
        }
      }
    });
    if (rowErrors.length) {
      errorIndices.push(rowIndex);
    }
    errors.push(...rowErrors);
  });
  return { errors, errorIndices };
};

export const normalizePreviousFileNumbers = (data) => {
  if (!Array.isArray(data)) return [];

  return data.map((num) => {
    if (typeof num === 'string') {
      return { ormFileNumber: num.toUpperCase() };
    }

    const ormFileNumber = num?.ormFileNumber?.toUpperCase?.() ?? num?.value?.toUpperCase?.() ?? '';

    return {
      previousFileNumberID: num?.previousFileNumberID ?? null,
      projectID: num?.projectID ?? null,
      projectVersion: num?.projectVersion ?? null,
      ormFileNumber,
    };
  });
};

export const buildPreviousFileNumbers = (data, isExistingProject, project) => {
  const normalized = normalizePreviousFileNumbers(data);
  if (!normalized.length) return [];

  if (isExistingProject) {
    return normalized.map((n) => ({
      previousFileNumberID: n.previousFileNumberID,
      projectID: n.projectID ?? project?.projectID ?? null,
      projectVersion: n.projectVersion ?? project?.projectVersion ?? null,
      ormFileNumber: n.ormFileNumber,
    }));
  }

  return normalized.map((n) => ({ ormFileNumber: n.ormFileNumber }));
};

export const mapToPermitApplicationRequestType = (requestTypeId, requestStatus) =>
  requestStatus === RequestStatus.Draft && [3, 4, 8, 9].includes(requestTypeId) ? 12 : requestTypeId;

export const siteDirectionsTooltipContent = (
  <>
    <p>
      Provide directions to the site from a known location or landmark. Include highway and street numbers as well as
      names. Also provide distances from known locations and any other information that would assist in locating the
      site. You may also provide description of the proposed project location, such as lot numbers, tract numbers, or
      you may choose to locate the proposed project location from a known point (such as the right descending bank of
      Smith Creek, one mile downstream from the Highway 14 bridge). If a large river or stream, include the river mile
      of the proposed project location if known.
    </p>
    <p>
      Example: The project/review area is located on the NSEW side of ROADWAY, XX miles/feet NSEW of the intersection of
      ROADWAY and ROADWAY, at Latitude XX.XXXXXX and Longitude XX.XXXXXX; in CITY, COUNTY, STATE.
    </p>
  </>
);
